import React, { useState } from 'react';
import { useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import styles from './ContentRow.module.css';
import { FiChevronLeft, FiChevronRight, FiPlay, FiFilm } from 'react-icons/fi';
import { useTrailerHoverPreview } from '../../hooks/useTrailerHoverPreview';
import HoverPreviewPopup from './HoverPreviewPopup';

export function CardSkeleton() {
  return <div className={`${styles.card} skeleton`} style={{ aspectRatio: '16/9' }} />;
}

export function VideoCard({ book, rank }) {
  const navigate = useNavigate();
  const [imgLoaded, setImgLoaded] = useState(false);
  const thumb = book.ThumbnailUrl || `https://picsum.photos/seed/${book.Id}/400/225`;

  // Continue Watching cards carry a resume episode — jump straight back into playback
  // instead of the movie detail page.
  const target = book._resumeEpisodeId ? `/watch/${book._resumeEpisodeId}` : `/movie/${book.Id}`;

  // ── Netflix-style floating trailer-preview popup ──────────────────────────
  const {
    anchorRef, videoRef, open, rect, previewReady, setPreviewReady,
    muted, setMuted, scheduleOpen, scheduleClose, cancelClose,
  } = useTrailerHoverPreview(book.TrailerUrl);

  const metaParts = [];
  if (book.ReleaseYear) metaParts.push(String(book.ReleaseYear));
  if (book.ContentType) metaParts.push(book.ContentType);
  if (book.EpisodeCount > 0) metaParts.push(`${book.EpisodeCount} eps`);
  if (book.CategoryName) metaParts.push(book.CategoryName);
  const meta = metaParts.join(' · ');

  return (
    <>
      <motion.div
        ref={anchorRef}
        className={`${styles.card} ${rank ? styles.rankedCard : ''}`}
        whileHover={{ scale: 1.06, zIndex: 20 }}
        transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
        onClick={() => navigate(target)}
        onMouseEnter={scheduleOpen}
        onMouseLeave={scheduleClose}
        tabIndex={0}
        role="button"
        aria-label={`Watch ${book.Title}`}
        onKeyDown={(e) => e.key === 'Enter' && navigate(target)}
      >
        {rank && <div className={styles.rankNum} aria-hidden="true">{rank}</div>}
        <div className={styles.cardInner}>
        <div className={styles.thumb}>
          {!imgLoaded && <div className="skeleton" style={{ position: 'absolute', inset: 0 }} />}
          <img
            src={thumb}
            alt={book.Title}
            loading="lazy"
            onLoad={() => setImgLoaded(true)}
            style={{ opacity: imgLoaded ? 1 : 0, transition: 'opacity 0.3s' }}
          />
          <div className={styles.thumbOverlay}>
            <button className={styles.playCircle} aria-label="Play">
              <FiPlay />
            </button>
          </div>
          {book.EpisodeCount > 0 && (
            <div className={styles.episodeBadge}>{book.EpisodeCount} eps</div>
          )}
          {book.ContentType && (
            <div className={`${styles.typeTag} ${book.ContentType === 'Series' ? styles.typeTagSeries : styles.typeTagMovie}`}>
              {book.ContentType === 'Series' ? 'Series' : 'Movie'}
            </div>
          )}
          {book.TrailerUrl && (
            <div
              className={styles.trailerBadge}
              onClick={(e) => { e.stopPropagation(); navigate(`/trailer/${book.Id}`); }}
              title="Watch Trailer"
            >
              <FiFilm /> Trailer
            </div>
          )}
        </div>

        <div className={styles.info}>
          <h3 className={styles.cardTitle}>{book.Title}</h3>
          <div className={styles.cardMeta}>
            {book.Rating && (
              <span style={{ fontSize: 11, fontWeight: 700, border: '1px solid var(--cast-border)', borderRadius: 4, padding: '0 6px' }}>
                {book.Rating}
              </span>
            )}
            {book.Director && <span className={styles.instructor}>Dir. {book.Director}</span>}
            {book.ReleaseYear && <span className={styles.year}>{book.ReleaseYear}</span>}
          </div>
          {book.CategoryName && (
            <span className={styles.category}>{book.CategoryName}</span>
          )}
        </div>
        </div>
      </motion.div>

      <HoverPreviewPopup
        open={open}
        rect={rect}
        videoRef={videoRef}
        trailerUrl={book.TrailerUrl}
        thumbnailUrl={thumb}
        previewReady={previewReady}
        onPreviewPlaying={() => setPreviewReady(true)}
        muted={muted}
        onToggleMute={() => setMuted((m) => !m)}
        title={book.Title}
        meta={meta}
        onMouseEnter={cancelClose}
        onMouseLeave={scheduleClose}
      />
    </>
  );
}

export default function ContentRow({ title, books = [], loading = false, accentColor, showRank = false }) {
  const rowRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(true);

  const checkScroll = useCallback(() => {
    const el = rowRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 8);
    setCanRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 8);
  }, []);

  const scroll = (dir) => {
    const el = rowRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.8;
    el.scrollBy({ left: dir === 'left' ? -amount : amount, behavior: 'smooth' });
  };

  if (!loading && books.length === 0) return null;

  return (
    <section className={`${styles.section} ${showRank ? styles.sectionRanked : ''}`}>
      <div className={styles.header}>
        <h2 className={styles.rowTitle} style={accentColor ? { '--accent': accentColor } : {}}>
          {title}
        </h2>
        <div className={styles.navBtns}>
          <button className={styles.navBtn} onClick={() => scroll('left')} disabled={!canLeft} aria-label="Scroll left">
            <FiChevronLeft />
          </button>
          <button className={styles.navBtn} onClick={() => scroll('right')} disabled={!canRight} aria-label="Scroll right">
            <FiChevronRight />
          </button>
        </div>
      </div>

      <div className={`${styles.row} ${showRank ? styles.rowRanked : ''}`} ref={rowRef} onScroll={checkScroll}>
        {loading
          ? Array.from({ length: 6 }).map((_, i) => <CardSkeleton key={i} />)
          : books.map((book, idx) => <VideoCard key={book.Id} book={book} rank={showRank ? idx + 1 : undefined} />)
        }
      </div>
    </section>
  );
}
